import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';

import { Grid, GridItem } from 'common/components/Grid';
import { HeadlineH7 } from 'common/components/Typography';
import { breakpoint } from 'common/theme/helper';
import { Card } from './Card';

const CardGridItem = styled(GridItem)<{ title: string }>`
  ${props =>
    props.title
      ? css`
          &:nth-of-type(-n + 2) {
            margin-top: 0;
          }
          ${breakpoint(0)(props)} {
            &:nth-of-type(-n + 3) {
              margin-top: 0;
            }
          }
          ${breakpoint(1)(props)} {
            &:nth-of-type(-n + 5) {
              margin-top: 0;
            }
          }
        `
      : css`
          &:nth-of-type(-n + 1) {
            margin-top: 0;
          }
          ${breakpoint(0)(props)} {
            &:nth-of-type(-n + 2) {
              margin-top: 0;
            }
          }
          ${breakpoint(1)(props)} {
            &:nth-of-type(-n + 4) {
              margin-top: 0;
            }
          }
        `};
`;

const cardGridItemStyles: Partial<ComponentProps<typeof CardGridItem>> = {
  w: [1, 1 / 2, 1 / 4],
  mt: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
};

const cardGridHeaderStyles: ComponentProps<typeof HeadlineH7> = {
  as: 'h3',
  mb: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
  mt: 'sp_0',
};

interface CardGridLayoutProps {
  title: Nullable<string>;
  titleId: string;
  items: Array<Omit<ComponentProps<typeof Card>, 'index'>>;
}

export const CardGridLayout = ({ title, titleId, items }: CardGridLayoutProps) => {
  return (
    <Grid data-corgi-id="card-grid" as="section" {...(title ? { 'aria-labelledby': titleId } : {})}>
      {title && (
        <GridItem>
          <HeadlineH7 id={titleId} {...cardGridHeaderStyles}>
            {title}
          </HeadlineH7>
        </GridItem>
      )}
      {items.map((item, index) => (
        <CardGridItem {...cardGridItemStyles} key={index} title={title ?? ''}>
          <Card index={index} {...item} />
        </CardGridItem>
      ))}
    </Grid>
  );
};
